import {
  getValSchemaFormDef,
  getValSchemaSections,
  getValSchemaSectionsForDisplay,
} from './valSchema';
// Deep import: barrel @hit/base pulls store/models and causes Form TDZ via form/validation.
import {DataService} from '@hit/base/src/api/data/DataService';
import yaml from 'js-yaml';
import Ajv from 'ajv';
const valSchemaFormDef = getValSchemaFormDef();
const valSchemaSections = getValSchemaSections();
const valSchemaSectionsForDisplay = getValSchemaSectionsForDisplay();

export function generateData(yamlFormDefinition) {
  const META_FIELDS = ['parent', 'formDate'];
  function getInput(inputDefinition, order) {
    // eslint-disable-next-line no-prototype-builtins
    if (inputDefinition.hasOwnProperty('data')) {
      throw new Error(
        `data key already defined: ${JSON.stringify(inputDefinition)}`,
      );
    }

    if (inputDefinition['type'] === 'list') {
      // contains sub-inputs
      let contentDefinition = {
        definition: Object.assign({}, inputDefinition, {
          content: {},
          order: order,
        }),
        data: [],
      };
      let contentOrder = 1;
      for (const [contentInputId, contentInputDefinition] of Object.entries(
        inputDefinition['content'],
      )) {
        let subInput = getInput(contentInputDefinition, contentOrder);
        // eslint-disable-next-line no-prototype-builtins
        if (subInput.hasOwnProperty('data')) {
          delete subInput['data'];
        }
        contentDefinition['definition']['content'][contentInputId] = subInput;
        contentOrder += 1;
      }
      if (contentDefinition['definition']['constraints'] === undefined) {
        contentDefinition['definition']['constraints'] = {};
      }
      return contentDefinition;
    } else {
      // eslint-disable-next-line no-prototype-builtins
      if (inputDefinition.hasOwnProperty('values')) {
        let valueOrder = 1;
        for (const [_valuesInputId, valuesInputDefinition] of Object.entries(
          inputDefinition['values'],
        )) {
          valuesInputDefinition['order'] = valueOrder;
          valueOrder += 1;
        }
      }

      let inputContent = {
        definition: Object.assign({}, inputDefinition, {order: order}),
      };
      if (inputDefinition['type'] !== 'label') {
        // eslint-disable-next-line no-prototype-builtins
        if (inputDefinition.hasOwnProperty('default')) {
          inputContent['data'] = inputDefinition['default'];
        } else if (inputDefinition?.constraints?.multiple) {
          inputContent['data'] = [];
        } else {
          inputContent['data'] = null;
        }
        if (inputContent['definition']['constraints'] === undefined) {
          inputContent['definition']['constraints'] = {};
        }
      }
      return inputContent;
    }
  }

  try {
    let yamlData = yaml.load(yamlFormDefinition);
    if (yamlData === null || Object.keys(yamlData).length === 0) {
      throw new Error('yaml definition is empty');
    }

    let dataDict = Object.assign({}, yamlData);

    // Process meta fields
    let metaFieldOrder = 1;
    for (let metaField in yamlData['meta']) {
      if (META_FIELDS.includes(metaField)) {
        dataDict['meta'][metaField] = getInput(
          yamlData['meta'][metaField],
          metaFieldOrder,
        );
        metaFieldOrder += 1;
      }
    }

    // Process sections
    let sectionOrder = 1;
    for (const [sectionId, sectionDefinition] of Object.entries(
      yamlData['sections'],
    )) {
      dataDict['sections'][sectionId] = {
        meta: Object.assign({}, sectionDefinition['meta'], {
          order: sectionOrder,
        }),
        content: {},
      };
      if (sectionDefinition['condition']) {
        dataDict['sections'][sectionId]['condition'] =
          sectionDefinition['condition'];
      }
      let sectionContentOrder = 1;
      for (const [inputId, inputDefinition] of Object.entries(
        sectionDefinition['content'],
      )) {
        dataDict['sections'][sectionId]['content'][inputId] = getInput(
          inputDefinition,
          sectionContentOrder,
        );
        sectionContentOrder += 1;
      }
      sectionOrder += 1;
    }
    return dataDict;
  } catch (e) {
    throw new Error(
      `Cannot initialize form data from yaml definition: [${e.constructor.name}] ${e.message}`,
    );
  }
}
export function isValidFormDefYaml(yamlFormDefinition) {
  const yamlObj = yaml.load(yamlFormDefinition);
  const ajv = new Ajv();
  const validate = ajv.compile(valSchemaFormDef);
  return validate(yamlObj);
}

export function isValidSectionsYaml(yamlSections, forDisplay = false) {
  const yamlObj = yaml.load(yamlSections);
  if (yamlObj.meta && yamlObj.meta.name === null) {
    yamlObj.meta.name = {};
  }
  const ajv = new Ajv();

  const validate = ajv.compile(
    forDisplay ? valSchemaSectionsForDisplay : valSchemaSections,
  );
  return validate(yamlObj);
}

export async function syncFormValidationTypeFromDefinition(formId) {
  const formResponse = await DataService.read('form', {
    attributes: 'data,form_definition_id',
    filters: {id: 'eq.' + formId},
  });
  const form = formResponse.data?.[0];
  if (!form?.form_definition_id) {
    return;
  }

  const defResponse = await DataService.read('form_definition', {
    attributes: 'definition',
    filters: {id: 'eq.' + form.form_definition_id},
  });
  const definitionYaml = defResponse.data?.[0]?.definition;
  if (!definitionYaml) {
    return;
  }

  const definition = yaml.load(definitionYaml);
  const definitionValidationType =
    definition?.meta?.validation?.type ?? 'submit';
  const formValidationType = form.data?.meta?.validation?.type;

  if (formValidationType === definitionValidationType) {
    return;
  }

  const updatedData = structuredClone(form.data);
  if (!updatedData.meta) {
    updatedData.meta = {};
  }
  if (!updatedData.meta.validation) {
    updatedData.meta.validation = {};
  }
  updatedData.meta.validation.type = definitionValidationType;

  await DataService.update('form', {id: 'eq.' + formId}, {data: updatedData});
}
